import { queryOptions, useQuery } from '@tanstack/react-query';
import {
  fetchBackgroundJobs,
  fetchBackgroundJobsCount,
} from '@/api/(admin)/background-jobs';
import type { BackgroundJobsQueryInput } from '../-types';
import { AUTO_REFRESH_MS } from '../-utils/constants';
import {
  toBackgroundJobsBody,
  toBackgroundJobsCountBody,
} from '../-utils/listParams';

/**
 * Shared by the route loader and the page hook. Keyed on the converted body so
 * both land on one cache entry. Refetches on an interval — "is it still
 * running" is the question the page answers — and React Query pauses that
 * while the tab is hidden.
 */
export function backgroundJobsQueryOptions(input: BackgroundJobsQueryInput) {
  const body = toBackgroundJobsBody(input);
  return queryOptions({
    queryKey: ['admin', 'background-jobs', 'list', body] as const,
    queryFn: () => fetchBackgroundJobs(body),
    refetchInterval: AUTO_REFRESH_MS,
  });
}

/**
 * The total runs once per filter: keyed on the filters alone, never stale and
 * never on the interval, so paging, sorting and auto-refresh reuse it. It can
 * lag behind new runs until the filters change or someone clicks Refresh —
 * fine for a page count, and it keeps the expensive query off every refresh.
 */
function backgroundJobsCountQueryOptions(input: BackgroundJobsQueryInput) {
  const body = toBackgroundJobsCountBody(input);
  return queryOptions({
    queryKey: ['admin', 'background-jobs', 'count', body] as const,
    queryFn: () => fetchBackgroundJobsCount(body),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useBackgroundJobs(input: BackgroundJobsQueryInput) {
  const list = useQuery(backgroundJobsQueryOptions(input));
  const count = useQuery(backgroundJobsCountQueryOptions(input));

  return {
    rows: list.data?.data ?? [],
    totalCount: count.data?.data.total ?? 0,
    isCapped: count.data?.data.capped ?? false,
    isLoading: list.isLoading,
    isError: list.isError,
    isFetching: list.isFetching || count.isFetching,
    refetch: () => {
      void list.refetch();
      void count.refetch();
    },
  };
}
